/**

Coded by www.techhighway.ca

*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";
import { useContext, useState } from "react";
import { NavLink } from "react-router-dom";

// @mui material components
import ExpandMore from "@mui/icons-material/ExpandMore";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import Collapse from "@mui/material/Collapse";
import Icon from "@mui/material/Icon";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import authPermission from "utility/auth-permissions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import { PreserveTable } from "context";

// Custom styles for the SidenavCollapse
import {
  collapseIcon,
  collapseIconBox,
  collapseItem,
  collapseText,
} from "components/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";

function SidenavCollapse({ icon, name, active, collapse, open, onClick, ...rest }) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const [openNested, setOpenNested] = useState(false);
  const collapseName = location.pathname.replace("/", "");
  const { hasPermission } = authPermission();
  const preserveTable = useContext(PreserveTable);

  const handleClick = (nestedIndex) => {
    setOpenNested(openNested === nestedIndex ? null : nestedIndex);
  };

  return (
    <>
      <ListItem component="li" onClick={onClick}>
        <MDBox
          {...rest}
          onClick={async () => {
            if (collapse?.length < 1) {
              await preserveTable.reset();
            }
          }}
          sx={(theme) =>
            collapseItem(theme, {
              active,
              transparentSidenav,
              whiteSidenav,
              darkMode,
              sidenavColor,
            })
          }
        >
          <ListItemIcon
            sx={(theme) =>
              collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active })
            }
          >
            {typeof icon === "string" ? (
              <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
            ) : (
              icon
            )}
          </ListItemIcon>

          <ListItemText
            primary={name}
            sx={(theme) =>
              collapseText(theme, {
                miniSidenav,
                transparentSidenav,
                whiteSidenav,
                active,
              })
            }
          />
          {collapse?.length > 0 ? open ? <ExpandMore /> : <KeyboardArrowLeftIcon /> : ""}
        </MDBox>
      </ListItem>
      {collapse?.length > 0 ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" sx={{ marginLeft: "15px" }}>
            {collapse.map(
              (
                { type, name, icon, title, noCollapse, key, href, route, collapse, target },
                index
              ) => {
                if (
                  hasPermission(route).view ||
                  hasPermission(href).view ||
                  hasPermission(name).view
                ) {
                  return (
                    <NavLink key={`navlink-${key}-${index}`} to={route} target={target || ""}>
                      <SidenavCollapse
                        name={name}
                        title={name}
                        icon={icon}
                        active={key === collapseName}
                        collapse={collapse || []}
                        target={target || ""}
                        open={openNested === index}
                        onClick={() => handleClick(index)}
                      />
                    </NavLink>
                  );
                }
              }
            )}
          </List>
        </Collapse>
      ) : (
        ""
      )}
    </>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
};

export default SidenavCollapse;
